/**
 * PATH: packages/job-hub/src/jobs/qa/useQaChecklistEditor.ts
 * FILE_ID: JOBHUB-QA-EDITOR-HOOK-001
 * PURPOSE: Every write the QA build surface can make, in one place.
 * WHY: The phone card list and the desktop table are two presentations of the
 *      SAME edit model. Moved VERBATIM out of QaChecklistTable so neither
 *      presentation owns the mutations; a second copy would drift and would
 *      trip the duplication gate.
 * LANDMINE: reorder and insert indices are AREA-WIDE, so `items` must stay the
 *      flat area sequence even though both views display it grouped. orderRef
 *      holds the latest sequence because handlers run after render.
 * RELATED:
 * - ./QaChecklistTable.tsx (desktop presentation)
 * - ../tabs/job-qa/phone/build/QaPhoneBuildCards.tsx (phone presentation)
 */
import { useCallback, useContext, useRef, useState } from 'react'
import { QueryClient, QueryClientContext, useQueryClient } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution, QaItemEdit, QaItemInstance } from './qaApi'
import { areaItems, checklistGroups } from './qaApiHelpers'
import { qaNeedsSignoff } from './qaHoldPoints'
import {
  insertIdAt,
  moveSibling,
  toSortOrderPayload,
} from './checklistTable'
import { placeBefore, sameParent } from './qaDragOrder'
import { QA_PENDING_DELETE_NOTICE } from '../tabs/job-qa/qaTabSelectors'
import { appendItem, holdQaArea, patchItem, releaseQaArea, reorderAreaItems } from '../tabs/job-qa/qaExecutionCache'
import { applyQaDelete } from '../tabs/job-qa/qaSelectionWrites'

export type QaRowOrigin = 'standard' | 'quote' | 'manual'

function placeItem(area: QaAreaExecution, item: QaItemInstance): QaAreaExecution {
  const checklistId = item.checklist_instance || area.checklists[0]?.id
  if (!checklistId) return area
  if (area.checklists.some((checklist) => checklist.items.some((row) => row.id === item.id))) {
    return patchItem(area, item)
  }
  return appendItem(area, checklistId, item)
}

export function useQaChecklistEditor({
  area,
  api,
  onMutated,
  checklistId = null,
  queryClient = null,
}: {
  area: QaAreaExecution
  api: QaApi
  onMutated: () => void
  checklistId?: string | null
  queryClient?: QueryClient | null
}) {
  const [fallbackClient] = useState(() => new QueryClient())
  const contextClient = useContext(QueryClientContext) ?? null
  // Reused from: @tanstack/react-query (useQueryClient)
  const liveClient = useQueryClient(queryClient ?? contextClient ?? fallbackClient)
  const client = liveClient === fallbackClient ? null : liveClient
  const items: QaItemInstance[] = areaItems(area)
  const groups = checklistGroups(area)
    .filter((group) => !checklistId || group.checklist.id === checklistId)
    .map((group, index) => ({ ...group, index }))

  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const orderRef = useRef(items)
  orderRef.current = items

  const publishArea = (next: (current: QaAreaExecution) => QaAreaExecution) => {
    if (!queryClient) return false
    queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', area.id], (current) => (
      current ? next(current) : current
    ))
    return true
  }

  const run = useCallback(async (
    key: string,
    fn: () => Promise<unknown>,
    opts?: { local?: boolean },
  ) => {
    setBusy(key)
    setError(null)
    try {
      await fn()
      if (!opts?.local) onMutated()
    } catch (e) {
      setError(
        (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data?.error ||
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'That change could not be saved. Please try again.',
      )
    } finally {
      setBusy(null)
    }
  }, [onMutated])

  const editCell = (item: QaItemInstance, fields: QaItemEdit) => {
    const key = 'has_hold_point' in fields
      ? `hold-${item.id}`
      : 'signoff_required' in fields ? `signoff-${item.id}` : `cell-${item.id}`
    void run(key, async () => {
      const saved = 'has_hold_point' in fields
        ? await api.setHoldPoint(item.id, !!fields.has_hold_point)
        : 'signoff_required' in fields
          ? await api.setSignoffRequired(item.id, !!fields.signoff_required)
          : await api.setItemContent(item.id, fields)
      publishArea((current) => patchItem(current, saved))
    }, { local: Boolean(queryClient) })
  }

  const flatIndex = (item: QaItemInstance) => orderRef.current.findIndex((i) => i.id === item.id)

  const reorderSibling = (item: QaItemInstance, dir: -1 | 1) => {
    const index = flatIndex(item)
    const next = moveSibling(orderRef.current, index, dir)
    if (next.map((i) => i.id).join() === orderRef.current.map((i) => i.id).join()) return
    const ids = next.map((row) => row.id)
    const previous = queryClient?.getQueryData<QaAreaExecution>(['qa-area-exec', area.id])
    holdQaArea(area.id)
    if (previous) publishArea(() => reorderAreaItems(previous, ids))
    void run('reorder', async () => {
      try {
        await api.reorderItems(toSortOrderPayload(ids))
      } catch (error) {
        if (previous) queryClient?.setQueryData(['qa-area-exec', area.id], previous)
        throw error
      } finally {
        releaseQaArea(area.id)
      }
    }, { local: Boolean(queryClient) })
  }

  const reorderTo = (item: QaItemInstance, beforeId: string) => {
    if (!sameParent(orderRef.current, item.id, beforeId)) return
    const placed = placeBefore(orderRef.current, item.id, beforeId)
    const ids = placed.map((row) => row.id)
    if (ids.join() === orderRef.current.map((row) => row.id).join()) return
    const previous = queryClient?.getQueryData<QaAreaExecution>(['qa-area-exec', area.id])
    holdQaArea(area.id)
    if (previous) publishArea(() => reorderAreaItems(previous, ids))
    void run('reorder', async () => {
      try {
        await api.reorderItems(toSortOrderPayload(ids))
      } catch (error) {
        if (previous) queryClient?.setQueryData(['qa-area-exec', area.id], previous)
        throw error
      } finally {
        releaseQaArea(area.id)
      }
    }, { local: Boolean(queryClient) })
  }

  /** Append a row (optionally as a sub-row) then reorder it into `targetIndex`. */
  const insertRow = (
    targetIndex: number,
    fields: QaItemEdit,
    parentItemId?: string | null,
    insertAfterNumber?: string | null,
    intoChecklistId?: string | null,
    origin?: QaRowOrigin,
  ) => {
    void run('insert', async () => {
      const created = await api.addItem(
        area.id,
        fields,
        parentItemId,
        insertAfterNumber,
        intoChecklistId ?? checklistId,
        origin,
      )
      const order = insertIdAt(orderRef.current.map((i) => i.id), created.id, targetIndex)
      await api.reorderItems(toSortOrderPayload(order))
      publishArea((current) => reorderAreaItems(placeItem(current, created), order))
    }, { local: Boolean(queryClient) })
  }

  const addOriginRow = (
    groupChecklistId: string,
    sectionItems: QaItemInstance[],
    origin: 'standard' | 'quote',
  ) => {
    const last = sectionItems[sectionItems.length - 1]
    const targetIndex = last ? flatIndex(last) + 1 : orderRef.current.length
    insertRow(targetIndex, {}, null, last?.item_number ?? null, groupChecklistId, origin)
  }

  const deleteItem = (item: QaItemInstance) =>
    void run(`del-${item.id}`, async () => {
      if (!client) return
      const result = await applyQaDelete({
        api,
        queryClient: client,
        inspectionId: null,
        executions: [area],
        targets: { items: [item.id], checklists: [], areas: [] },
      })
      if (result.pendingItems.includes(item.id)) setError(QA_PENDING_DELETE_NOTICE)
    }, { local: true })

  const addRow = (onCreated?: (itemId: string) => void) =>
    void run('add', async () => {
      const created = await api.addItem(area.id, {}, null, null, checklistId)
      onCreated?.(created.id)
      publishArea((current) => placeItem(current, created))
    }, { local: Boolean(queryClient) })

  return {
    items,
    groups,
    busy,
    error,
    setError,
    run,
    editCell,
    flatIndex,
    reorderSibling,
    reorderTo,
    insertRow,
    addOriginRow,
    deleteItem,
    addRow,
  }
}

export type QaChecklistEditor = ReturnType<typeof useQaChecklistEditor>

/**
 * The row-menu handlers, built from the editor. A row inserted next to another
 * row inherits its checklist, parent and origin, or it lands in the wrong
 * section; that inheritance is the reason this is shared rather than retyped
 * per surface.
 */
export function buildQaChecklistRowActions(editor: QaChecklistEditor, item: QaItemInstance) {
  const origin: QaRowOrigin = item.origin === 'standard' ? 'standard' : 'quote'
  const sibling = [item.parent_item ?? null, item.item_number, item.checklist_instance] as const
  return {
    onInsertAbove: () =>
      editor.insertRow(editor.flatIndex(item), {}, sibling[0], null, sibling[2], origin),
    onInsertBelow: () =>
      editor.insertRow(editor.flatIndex(item) + 1, {}, sibling[0], sibling[1], sibling[2], origin),
    onInsertSubRow: () =>
      editor.insertRow(editor.flatIndex(item) + 1, {}, item.template_item ?? null),
    onDuplicate: () =>
      editor.insertRow(
        editor.flatIndex(item) + 1,
        {
          title: item.title,
          product: item.product ?? '',
          requirement_text: item.requirement_text ?? '',
          acceptance_criteria: item.acceptance_criteria ?? '',
          has_hold_point: !!item.has_hold_point,
        },
        sibling[0],
        sibling[1],
        sibling[2],
        origin,
      ),
    onToggleHold: () => editor.editCell(item, { has_hold_point: !item.has_hold_point }),
    onToggleSignoff: () => editor.editCell(item, { signoff_required: !qaNeedsSignoff(item) }),
    onMoveUp: () => editor.reorderSibling(item, -1),
    onMoveDown: () => editor.reorderSibling(item, 1),
    onDelete: () => editor.deleteItem(item),
  }
}
